import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { prefetchAll } from "../utils/prefetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [appConfig, setAppConfig] = useState(null);

  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/setup/config`);
        setAppConfig(response.data);
      } catch (e) {
        setAppConfig({ app_name: 'Gestion Planning', company_name: null, logo_url: null, primary_color: '#2563EB' });
      }
    };
    fetchConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      // Stocker le token et les infos utilisateur
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      // Mettre à jour le contexte
      login(response.data.user);
      
      // Précharger les données en arrière-plan après connexion
      prefetchAll();
      
      // Rediriger selon le rôle
      const userData = response.data.user;
      if (userData.role === "super_admin" || userData.is_super_admin) {
        navigate("/super-admin");
      } else {
        navigate("/");
      }
      
    } catch (error) {
      console.error("Erreur de connexion:", error);
      setError(error.response?.data?.detail || "Email ou mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            {appConfig?.logo_url ? (
              <img 
                src={appConfig.logo_url}
                alt={`${appConfig.company_name || appConfig.app_name} Logo`}
                className="mx-auto mb-4 w-32 h-32 object-contain"
              />
            ) : (
              <div className="mx-auto mb-4 w-24 h-24 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
                <span className="text-white text-4xl font-bold">
                  {(appConfig?.company_name || appConfig?.app_name || 'P').charAt(0)}
                </span>
              </div>
            )}
            <h1 className="text-3xl font-bold mb-2" style={{ color: appConfig?.primary_color || '#2563EB' }}>
              {appConfig?.company_name || appConfig?.app_name || 'Gestion Planning'}
            </h1>
          </div>

          {/* Formulaire de connexion */}
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                ❌ {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="form-input w-full"
                placeholder="votre@email.com"
                disabled={loading}
                data-testid="login-email-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="form-input w-full"
                placeholder="••••••••"
                disabled={loading}
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>🔐 Connexion sécurisée</p>
          </div>
        </div>

        {/* Info sous la card */}
        <div className="mt-4 text-center text-sm text-gray-600">
          <p>Mot de passe oublié ? Contactez votre administrateur</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
